import { hashKey, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect } from "react";
import { currentUserWorkbookAccessKey } from "@/business/query";
import { useApi } from "@/core/api";
import type { PayrollRoster } from "@/payroll/roster/types";
import { isPayrollAccessError } from "@/payroll/teams/access-error";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { payrollRosterKey, payrollRosterVersionKey } from "./keys";
import { payrollRosterPath } from "./paths";
import { useRosterViewer } from "./use-roster-viewer";

export const payrollRosterVersionQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  version: number | null,
  enabled = true,
) => ({
  queryKey: payrollRosterVersionKey(businessId, version),
  queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
    if (!businessId || version == null) throw new Error("Version is required");

    const response = await api.get<PayrollRoster | null>(
      payrollRosterPath(businessId),
      { params: { version }, signal },
    );

    return response.status === 204 ? null : response.data;
  },
  enabled: enabled && !!businessId && version != null,
  retry: false,
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchInterval: 30_000,
});

export const usePayrollRosterVersion = (
  businessId: string | null,
  version: number | null,
  enabled = true,
) => {
  const api = useApi();

  return useAuthorizedRosterQuery(
    businessId,
    payrollRosterVersionQueryOptions(api, businessId, version, enabled),
    version,
  );
};

export const payrollRosterQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
) => ({
  queryKey: payrollRosterKey(businessId),
  queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
    if (!businessId) throw new Error("Business is required");

    const response = await api.get<PayrollRoster | null>(
      payrollRosterPath(businessId),
      { signal },
    );

    return response.status === 204 ? null : response.data;
  },
  enabled: !!businessId,
  staleTime: 0,
  refetchOnMount: "always" as const,
  retry: false,
});

const useAuthorizedRosterQuery = (
  businessId: string | null,
  options: Omit<ReturnType<typeof payrollRosterQueryOptions>, "queryKey">,
  version?: number | null,
) => {
  const client = useQueryClient();
  const { access, viewer } = useRosterViewer(businessId);
  const allowed = access.canReadRoster;
  const queryKey =
    version === undefined
      ? payrollRosterKey(businessId, viewer)
      : payrollRosterVersionKey(businessId, version, viewer);
  const queryHash = hashKey(queryKey);
  useEffect(
    () => () => {
      queueMicrotask(() =>
        client.removeQueries({
          predicate: (cached) =>
            cached.queryHash === queryHash && cached.getObserversCount() === 0,
        }),
      );
    },
    [client, queryHash],
  );
  const query = useQuery({
    ...options,
    queryKey,
    enabled: options.enabled && allowed,
    gcTime: 0,
    refetchInterval: 30_000,
    queryFn: async (context) => {
      try {
        return await options.queryFn(context);
      } catch (error) {
        if (isPayrollAccessError(error) && !context.signal.aborted) {
          if (error.status === 404) {
            client.setQueryData(queryKey, null);
            throw error;
          }
          await client.cancelQueries({
            queryKey: payrollRosterKey(businessId),
            predicate: (cached) => cached.queryHash !== queryHash,
          });
          client.setQueriesData(
            { queryKey: payrollRosterKey(businessId) },
            null,
          );
          if (error.status === 403) {
            void client.invalidateQueries({
              queryKey: currentUserWorkbookAccessKey(businessId, viewer.userId),
            });
          }
        }
        throw error;
      }
    },
  });
  useEffect(() => {
    if (
      allowed ||
      !businessId ||
      (!access.isError && !access.isFetchedAfterMount)
    )
      return;
    let active = true;
    void client
      .cancelQueries({ queryKey: payrollRosterKey(businessId) })
      .then(() => {
        if (active)
          client.setQueriesData(
            { queryKey: payrollRosterKey(businessId) },
            null,
          );
      });
    return () => {
      active = false;
    };
  }, [allowed, access.isError, access.isFetchedAfterMount, businessId, client]);
  const result = currentPayrollResult(query);
  return {
    ...result,
    data: allowed ? result.data : undefined,
    isLoading: result.isLoading || (options.enabled && access.isAccessLoading),
  };
};

export const usePayrollRoster = (businessId: string | null) => {
  const api = useApi();
  return useAuthorizedRosterQuery(
    businessId,
    payrollRosterQueryOptions(api, businessId),
  );
};
